//import '@reach/alert-dialog/styles.css';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveModal } from '../../../../actions/modalActions';
import { addEmail } from '../../../../actions/userActions';
import { logout } from '../../../../actions/userProfileActions';
import {
	CHANGE_ADDRESS_MODAL,
	CHANGE_PASSWORD_MODAL,
	MY_PROFILE_MODAL,
	VERIFY_PHONE_MODAL,
} from '../../../../constants/documentTypes/modals/modals';
import { ReactComponent as IconPencil } from '../../../../icons/pencil.svg';
import { ReactComponent as IconUser } from '../../../../icons/user.svg';
import IUserProfileReducer from '../../../../interfaces/reducers/userProfileReducer';
import { IStore } from '../../../../reducers';
import Button from '../../../base/button';
import { RawLoader } from '../../../loading/loading';
import { A, H1, H4, H5, H6, P } from '../../../base/typography';
import * as StyledSettings from '../settings.styles';

interface Props {
	forceVerifyEmail?: boolean;
}

const UserProfile: FC<Props> = ({ forceVerifyEmail = false }) => {
	const { t } = useTranslation('myShipments');
	const dispatch = useDispatch<any>();
	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { userProfile } = userProfileReducer;

	const hideIcelandicCountryCode = phoneNumber => {
		if (phoneNumber && phoneNumber.length === 11 && phoneNumber.substring(0, 4) === '+354')
			return phoneNumber.substring(4);
		return phoneNumber;
	};

	if (!userProfile) return <RawLoader />;

	return (
		<>
			<StyledSettings.HeaderMain>
				<H1>{t('navigation.settings')}</H1>
			</StyledSettings.HeaderMain>
			{forceVerifyEmail && (
				<StyledSettings.HeaderSub>
					<H4>{t('page-settings.verifyEmailForceHeader')}</H4>
					<H5>{t('page-settings.verifyEmailForce')}</H5>
				</StyledSettings.HeaderSub>
			)}

			<StyledSettings.Card>
				<StyledSettings.CardHeader>
					<StyledSettings.IconHeader>
						<IconUser />
						<H6>{userProfile.name}</H6>
					</StyledSettings.IconHeader>
					{userProfile.hasPassword && (
						<StyledSettings.LinkGroup>
							<A onClick={() => dispatch(setActiveModal(CHANGE_PASSWORD_MODAL))}>
								<IconPencil title={t('page-settings.changePassword')} />
								{t('page-settings.changePassword')}
							</A>
						</StyledSettings.LinkGroup>
					)}
				</StyledSettings.CardHeader>
				<StyledSettings.UserInfo>
					{forceVerifyEmail ? (
						<>
							<StyledSettings.UserInfoItem>
								<div>
									<P black>{t('email')}</P>
									<StyledSettings.Email>{userProfile.email}</StyledSettings.Email>
								</div>
								<A onClick={() => dispatch(setActiveModal(MY_PROFILE_MODAL, 'EMAIL'))}>
									<IconPencil title={t('change')} />
								</A>
							</StyledSettings.UserInfoItem>
						</>
					) : (
						<>
							<StyledSettings.UserInfoItem>
								<div>
									<P black>{t('email')}</P>
									<StyledSettings.Email>{userProfile.email}</StyledSettings.Email>
								</div>
								<A onClick={() => dispatch(setActiveModal(MY_PROFILE_MODAL, 'EMAIL'))}>
									<IconPencil title={t('change')} />
								</A>
							</StyledSettings.UserInfoItem>
							<StyledSettings.UserInfoItem>
								<div>
									<P black>{t('phone')}</P>
									<span>{hideIcelandicCountryCode(userProfile?.phoneNumber)}</span>
								</div>
								<A onClick={() => dispatch(setActiveModal(MY_PROFILE_MODAL, 'PHONE'))}>
									<IconPencil title={t('change')} />
								</A>
							</StyledSettings.UserInfoItem>
							<StyledSettings.UserInfoItem>
								<P black>{t('address')}</P>
								<span>
									{userProfile.currentAddress?.street1 +
										', ' +
										userProfile.currentAddress?.postalCode +
										' ' +
										userProfile.currentAddress?.city}
								</span>
								<A onClick={() => dispatch(setActiveModal(CHANGE_ADDRESS_MODAL))}>
									<IconPencil title={t('change')} />
								</A>
							</StyledSettings.UserInfoItem>
						</>
					)}
				</StyledSettings.UserInfo>
			</StyledSettings.Card>

			{(userProfile.email && !userProfile.emailVerified) ||
			(userProfile.phoneNumber && !userProfile.phoneNumberVerified) ? (
				<StyledSettings.VerifyBanner>
					<P>{t('page-settings.verifyMessage')}</P>
					<div>
						{!userProfile.emailVerified && (
							<Button primary onClick={() => dispatch(addEmail(userProfile.userId, userProfile.email))}>
								{t('page-settings.verifyEmail')}
							</Button>
						)}
						{userProfile?.phoneNumber && !userProfile.phoneNumberVerified && (
							<Button primary onClick={() => dispatch(setActiveModal(VERIFY_PHONE_MODAL))}>
								{t('page-settings.verifyPhoneNumber')}
							</Button>
						)}
					</div>
				</StyledSettings.VerifyBanner>
			) : null}

			{!userProfile.emailVerified && forceVerifyEmail && (
				<StyledSettings.SkipLink2 onClick={() => dispatch(logout())} logout>
					{t('navigation.logout')}
				</StyledSettings.SkipLink2>
			)}
		</>
	);
};

export default UserProfile;
